// @ts-check
// Diálogos da LGPD não consentida (2.0.0, contrato 09 de campanhas base, seção 6.9): "Marcar LGPD não consentida", com
// observação opcional, e "Registrar consentimento", com a origem, o que a pessoa disse e, para menor, o responsável
// adulto da mesma família que autorizou (o motor confere de novo, RISCO-16).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { findManyRecords } from '../../data/rest.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { markMarketingOptOut, registerMarketingConsent } from '../../data/marketing-consent.js';
import { canReadObjectRecords } from '../../lib/permissions.js';
import { getRecordLabel } from '../../lib/field-metadata.js';
import { Modal } from '../../ui/modal.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';
import {
  MARKETING_CONSENT_EVIDENCE_MAXIMUM_LENGTH, MARKETING_CONSENT_EVIDENCE_MINIMUM_LENGTH, MARKETING_OPT_OUT_NOTE_MAXIMUM_LENGTH,
  TEAM_MARKETING_CONSENT_SOURCES, isMinorPerson,
} from '../../../../shared/privacy/marketing-consent-rules.js';
import { isGuardianHouseholdRole } from '../../../../shared/households/guardian-rule.js';

/** @typedef {import('../../data/marketing-consent.js').MarketingStatus} MarketingStatus */

/** Erro dentro do diálogo: o responsável recusado pelo motor tem texto próprio. @param {unknown} error */
function dialogErrorMessage(error) {
  if (error instanceof ApiError && error.details?.reason === 'GRANTOR_NOT_GUARDIAN') return t('marketingConsent.registerDialog.guardianInvalid');
  return getErrorMessage(error);
}

/**
 * @param {{ personId: string, personLabel: string, onDone: (status: MarketingStatus) => void, onClose: () => void }} props
 */
export function MarkMarketingOptOutDialog({ personId, personLabel, onDone, onClose }) {
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const isTooLong = note.trim().length > MARKETING_OPT_OUT_NOTE_MAXIMUM_LENGTH;
  const confirm = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const status = await markMarketingOptOut(personId, { note: note.trim() });
      showSuccessSnackbar(t('marketingConsent.markDialog.done'));
      onDone(status);
    } catch (caught) {
      setError(dialogErrorMessage(caught));
      setIsSaving(false);
    }
  };
  return html`<${Modal}
    title=${t('marketingConsent.markDialog.title')}
    subtitle=${t('marketingConsent.markDialog.description', { name: personLabel })}
    confirmLabel=${t('marketingConsent.markDialog.confirm')}
    confirmAccent="danger"
    isConfirmDisabled=${isTooLong}
    isLoading=${isSaving}
    onClose=${onClose}
    onConfirm=${confirm}
  >
    <div class="marketing-consent-dialog" data-testid="marketing-opt-out-dialog">
      <label class="form-field">
        <span class="form-field-label">${t('marketingConsent.markDialog.noteLabel')}</span>
        <textarea class="text-input marketing-consent-textarea" rows="3" value=${note} maxLength=${MARKETING_OPT_OUT_NOTE_MAXIMUM_LENGTH}
          placeholder=${t('marketingConsent.markDialog.notePlaceholder')} data-error=${isTooLong ? 'true' : undefined}
          onInput=${(/** @type {Event} */ event) => setNote(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>
        <span class="input-hint" data-danger=${isTooLong ? 'true' : undefined}>
          ${t('marketingConsent.markDialog.noteCount', { count: note.trim().length, maximum: MARKETING_OPT_OUT_NOTE_MAXIMUM_LENGTH })}
        </span>
      </label>
      ${error ? html`<p class="marketing-consent-dialog-error" role="alert">${error}</p>` : null}
    </div>
  <//>`;
}

/**
 * Responsáveis adultos das famílias da pessoa (membros que não são aluno nem dependente), para o consentimento de menor.
 * Sem leitura dos membros da família, a lista fica vazia e o motor recusa.
 * @param {string} personId @returns {Promise<Array<{ id: string, label: string }>>}
 */
async function loadFamilyGuardians(personId) {
  const memberObject = objectsByNameSingular.value.get('householdMember');
  const personObject = objectsByNameSingular.value.get('person');
  if (!memberObject || !personObject || !canReadObjectRecords(memberObject)) return [];
  const own = await findManyRecords(memberObject, { filter: { field: 'personId', operator: 'eq', value: personId }, limit: 20, depth: 0 });
  const householdIds = [...new Set(own.records.map((member) => member.householdId).filter(Boolean))];
  if (householdIds.length === 0) return [];
  const members = await findManyRecords(memberObject, { filter: { field: 'householdId', operator: 'in', value: householdIds }, limit: 100, depth: 1 });
  const now = new Date().toISOString();
  /** @type {Map<string, { id: string, label: string }>} */
  const guardians = new Map();
  for (const member of members.records) {
    const person = member.person;
    if (!member.personId || member.personId === personId || !person || !isGuardianHouseholdRole(member.householdRole ?? null)) continue;
    if (isMinorPerson(person, now) || person.anonymizedAt) continue;
    guardians.set(member.personId, { id: member.personId, label: getRecordLabel(personObject, person) });
  }
  return [...guardians.values()].sort((first, second) => first.label.localeCompare(second.label, 'pt-BR'));
}

/**
 * @param {{ personId: string, personLabel: string, isMinor: boolean, onDone: (status: MarketingStatus) => void, onClose: () => void }} props
 */
export function RegisterMarketingConsentDialog({ personId, personLabel, isMinor, onDone, onClose }) {
  const [source, setSource] = useState(/** @type {string} */ (TEAM_MARKETING_CONSENT_SOURCES[0]));
  const [evidence, setEvidence] = useState('');
  const [guardianId, setGuardianId] = useState('');
  const [guardians, setGuardians] = useState(/** @type {Array<{ id: string, label: string }> | null} */ (isMinor ? null : []));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));

  useEffect(() => {
    if (!isMinor) return undefined;
    let isCancelled = false;
    loadFamilyGuardians(personId)
      .then((found) => {
        if (isCancelled) return;
        setGuardians(found);
        if (found.length === 1) setGuardianId(found[0].id);
      })
      .catch(() => !isCancelled && setGuardians([]));
    return () => {
      isCancelled = true;
    };
  }, [personId, isMinor]);

  const evidenceLength = evidence.trim().length;
  const isEvidenceValid = evidenceLength >= MARKETING_CONSENT_EVIDENCE_MINIMUM_LENGTH && evidenceLength <= MARKETING_CONSENT_EVIDENCE_MAXIMUM_LENGTH;
  const isGuardianMissing = isMinor && !guardianId;
  const confirm = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const status = await registerMarketingConsent(personId, { source, evidence: evidence.trim(), grantedByPersonId: isMinor ? guardianId : null });
      showSuccessSnackbar(t('marketingConsent.registerDialog.done'));
      onDone(status);
    } catch (caught) {
      setError(dialogErrorMessage(caught));
      setIsSaving(false);
    }
  };

  return html`<${Modal}
    title=${t('marketingConsent.registerDialog.title')}
    subtitle=${t('marketingConsent.registerDialog.description', { name: personLabel })}
    confirmLabel=${t('marketingConsent.registerDialog.confirm')}
    isConfirmDisabled=${!isEvidenceValid || isGuardianMissing}
    isLoading=${isSaving}
    onClose=${onClose}
    onConfirm=${confirm}
  >
    <div class="marketing-consent-dialog" data-testid="marketing-consent-dialog">
      <label class="form-field">
        <span class="form-field-label">${t('marketingConsent.registerDialog.sourceLabel')}</span>
        <select class="text-input" value=${source} onChange=${(/** @type {Event} */ event) => setSource(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
          ${TEAM_MARKETING_CONSENT_SOURCES.map((option) => html`<option key=${option} value=${option}>${t(`marketingConsent.registerDialog.sources.${option}`)}</option>`)}
        </select>
      </label>
      <label class="form-field">
        <span class="form-field-label">${t('marketingConsent.registerDialog.evidenceLabel')}</span>
        <textarea class="text-input marketing-consent-textarea" rows="4" value=${evidence} maxLength=${MARKETING_CONSENT_EVIDENCE_MAXIMUM_LENGTH}
          onInput=${(/** @type {Event} */ event) => setEvidence(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>
        <span class="input-hint">${t('marketingConsent.registerDialog.evidenceHint')} ${t('marketingConsent.registerDialog.evidenceCount', { count: evidenceLength, maximum: MARKETING_CONSENT_EVIDENCE_MAXIMUM_LENGTH })}</span>
      </label>
      ${isMinor
        ? html`<label class="form-field">
            <span class="form-field-label">${t('marketingConsent.registerDialog.guardianLabel')}</span>
            ${guardians !== null && guardians.length === 0
              ? html`<span class="input-hint" data-danger="true">${t('marketingConsent.registerDialog.guardianEmpty')}</span>`
              : html`<select class="text-input" value=${guardianId} disabled=${guardians === null}
                  onChange=${(/** @type {Event} */ event) => setGuardianId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
                  <option value="">${t('marketingConsent.registerDialog.guardianPlaceholder')}</option>
                  ${(guardians ?? []).map((guardian) => html`<option key=${guardian.id} value=${guardian.id}>${guardian.label}</option>`)}
                </select>
                <span class="input-hint">${t('marketingConsent.registerDialog.guardianRequired')}</span>`}
          </label>`
        : null}
      <p class="marketing-consent-dialog-note">${t('marketingConsent.registerDialog.confirmNote')}</p>
      ${error ? html`<p class="marketing-consent-dialog-error" role="alert">${error}</p>` : null}
    </div>
  <//>`;
}
